/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import React, { useState } from "react";
import DocumentPropertiesCSS from "./DocumentProperties.css";
import { getBasicDocProperties, getAdvanceDocProperties } from "utils/DocumentUtils";
import { getDocumentRestrictions } from "utils/DocPermissionsUtils";
import classNames from "classnames";
import { documentPropertiesMenuItems } from "constants/enums";
import { trackDocPropertiesTabClicked } from "analytics/events";
import { providers } from "dc-core";
import * as stores from "stores";

const convertListToComponent = (propertiesList, params) => {
  const { classNameForVal, classNameForTitle, classNameForHeading, headingId, formatMessage } = params;
  const PropertiesListComp = propertiesList.map(item => {
    const { key, value } = item;
    const keyTestId = `doc-properties-key-${key}`;
    const valueTestId = `doc-properties-value-${key}`;
    return (
      <div className={DocumentPropertiesCSS.mainSectionRow}>
        <span className={DocumentPropertiesCSS.propertiesKey} data-testid={keyTestId}>
          {formatMessage({ id: `dialog.documentProperties.${key}` })} :
        </span>
        <span className={classNameForVal} data-testid={valueTestId}>
          {value || "-"}
        </span>
      </div>
    );
  });

  const propertiesTestId = `doc-properties-${headingId}`;
  return (
    <div className={classNameForTitle}>
      <div className={classNameForHeading} data-testid={propertiesTestId}>
        {formatMessage({ id: `dialog.documentProperties.${headingId}` })}
      </div>
      <div className={DocumentPropertiesCSS.mainSection}>{PropertiesListComp}</div>
    </div>
  );
};

const BasicPropertiesComp = ({ formatMessage }) => {
  const basicProperties = getBasicDocProperties();
  const { fileName, createdOn, modifiedOn, title, author, creatorApp, subject, keywords } = basicProperties;
  const basicPropertiesList = [
    { key: "filname", value: fileName },
    { key: "createdOn", value: createdOn },
    { key: "modifiedOn", value: modifiedOn },
    { key: "pdfTitle", value: title },
    { key: "author", value: author },
    { key: "creatorApp", value: creatorApp },
    { key: "subject", value: subject },
    { key: "keywords", value: keywords }
  ];

  const basicPropertiesListComp = convertListToComponent(basicPropertiesList, {
    classNameForVal: DocumentPropertiesCSS.propertiesValue,
    classNameForTitle: DocumentPropertiesCSS.basic,
    classNameForHeading: DocumentPropertiesCSS.headingRightSection,
    headingId: "basic",
    formatMessage
  });

  return basicPropertiesListComp;
};

const AdvancePropertiesComp = ({ formatMessage }) => {
  const advanceProperties = getAdvanceDocProperties();
  const yes = <span>{formatMessage({ id: "dialog.documentProperties.yes" })}</span>;
  const no = <span>{formatMessage({ id: "dialog.documentProperties.no" })}</span>;
  const { pdfProducer, pdfVersion, location, fileSize, pageSize, numPages, isTaggedPDF } = advanceProperties;
  const [fileLocation, setFileLocation] = useState(location);
  if (providers && providers.integrationProvider) {
    try {
      providers
        .integrationProvider()
        .then(async integrationFactory => {
          integrationFactory
            .getFilePath()
            .then(filePath => {
              setFileLocation(filePath);
              stores.appStore.setFileLocation(filePath);
            })
            .catch(() => {});
        })
        .catch(() => {});
    } catch (err) {}
  }
  const advancePropertiesList = [
    { key: "producer", value: pdfProducer },
    { key: "version", value: pdfVersion },
    { key: "location", value: fileLocation },
    { key: "fileSize", value: fileSize },
    { key: "pageSize", value: pageSize },
    { key: "numPages", value: numPages },
    { key: "taggedPDF", value: isTaggedPDF ? yes : no }
  ];

  const advancePropertiesListComp = convertListToComponent(advancePropertiesList, {
    classNameForVal: DocumentPropertiesCSS.propertiesValue,
    classNameForTitle: DocumentPropertiesCSS.advance,
    classNameForHeading: DocumentPropertiesCSS.headingRightSection,
    headingId: "advanced",
    formatMessage
  });

  return advancePropertiesListComp;
};

const DocPermissionsComp = ({ formatMessage }) => {
  const docRestrictions = getDocumentRestrictions();
  const {
    modifyingContentAllowed,
    commentingAllowed,
    printingAllowed,
    copyingContentAllowed,
    copyingContentForAccessibilityAllowed,
    pageExtractionAllowed,
    formFillingAllowed,
    signingAllowed,
    documentAssemblyAllowed,
    templatePagesCreation
  } = docRestrictions;

  const getVal = flag => {
    const perm = flag ? "allowed" : "notAllowed";
    return formatMessage({ id: `dialog.documentProperties.${perm}` });
  };

  const docRestrictionsList = [
    { key: "changingDocument", value: getVal(modifyingContentAllowed) },
    { key: "documentCommenting", value: getVal(commentingAllowed) },
    { key: "printing", value: getVal(printingAllowed) },
    { key: "contentCopying", value: getVal(copyingContentAllowed) },
    { key: "contentCopyingAccessibility", value: getVal(copyingContentForAccessibilityAllowed) },
    { key: "pageExtraction", value: getVal(pageExtractionAllowed) },
    { key: "formFilling", value: getVal(formFillingAllowed) },
    { key: "signing", value: getVal(signingAllowed) },
    { key: "documentAssembly", value: getVal(documentAssemblyAllowed) }
    // { key: "templatePagesCreation", value: getVal(templatePagesCreation) }
  ];

  const docRestrictionsListComp = convertListToComponent(docRestrictionsList, {
    classNameForVal: DocumentPropertiesCSS.docRestrictionsValue,
    classNameForTitle: classNames(DocumentPropertiesCSS.right, DocumentPropertiesCSS.docRestrictions),
    classNameForHeading: DocumentPropertiesCSS.headingRightSection,
    headingId: "restrictionSummary",
    formatMessage
  });

  return docRestrictionsListComp;
};

const PropertiesContainer = ({ formatMessage, activeItem }) => {
  switch (activeItem) {
    case documentPropertiesMenuItems.DESCRIPTION:
      return (
        <div className={DocumentPropertiesCSS.right}>
          <BasicPropertiesComp formatMessage={formatMessage} />
          <AdvancePropertiesComp formatMessage={formatMessage} />
        </div>
      );
    case documentPropertiesMenuItems.PERMISSIONS:
      return <DocPermissionsComp formatMessage={formatMessage} />;
    default:
      return null;
  }
};

const MenuItems = ({ formatMessage, activeItem, onClick }) => {
  const descClassName =
    activeItem === documentPropertiesMenuItems.DESCRIPTION
      ? classNames(DocumentPropertiesCSS.leftItems, DocumentPropertiesCSS.leftItemSelected)
      : DocumentPropertiesCSS.leftItems;

  const permClassName =
    activeItem === documentPropertiesMenuItems.PERMISSIONS
      ? classNames(DocumentPropertiesCSS.leftItems, DocumentPropertiesCSS.leftItemSelected)
      : DocumentPropertiesCSS.leftItems;

  return (
    <div className={DocumentPropertiesCSS.left} role="tablist">
      <div className={DocumentPropertiesCSS.headingLeftSection}>
        {formatMessage({ id: "dialog.documentProperties.properties" })}
      </div>
      <div
        role="tab"
        tabIndex="0"
        className={descClassName}
        onClick={() => onClick(documentPropertiesMenuItems.DESCRIPTION)}
        data-testid="doc-properties-description-tab"
      >
        {formatMessage({ id: "dialog.documentProperties.description" })}
      </div>
      <div
        role="tab"
        tabIndex="0"
        className={permClassName}
        onClick={() => onClick(documentPropertiesMenuItems.PERMISSIONS)}
        data-testid="doc-properties-permission-tab"
      >
        {formatMessage({ id: "dialog.documentProperties.permissions" })}
      </div>
    </div>
  );
};

export const DocPropertiesComponent = ({ formatMessage, activeElem = documentPropertiesMenuItems.DESCRIPTION }) => {
  const [activeItem, setActiveItem] = useState(activeElem);
  const onClick = item => {
    setActiveItem(item);
    trackDocPropertiesTabClicked(item);
  };
  return (
    <div className={DocumentPropertiesCSS.container}>
      <MenuItems formatMessage={formatMessage} activeItem={activeItem} onClick={onClick} />
      <PropertiesContainer formatMessage={formatMessage} activeItem={activeItem} />
    </div>
  );
};
